import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import BoardGrid from "../components/BoardGrid";
import Timer from "../components/Timer";
import {
  getCurrentUid,
  joinRoom,
  revealAndScore,
  submitInterpretation,
  updateRoom,
  watchRoom,
  watchTurns,
  writeTurn,
} from "../game/rooms";
import { isFirebaseConfigured } from "../firebase";
import { CODES, getRandomCode } from "../data/codebook";
import { watchCustomCodes, type CustomCode } from "../data/custom-codes";
import { scoreSolo, scoreTurnCoded, syntheticCodeFromTurn } from "../game/scoring";
import { getOrderedTokensForLang } from "../data/codebook-helpers";
import { formatSentence } from "../game/format";
import {
  getStoredKey,
  setStoredKey,
  suggestCode,
  suggestedTokensToSentenceTokens,
} from "../game/gemini";
import SentenceTrack from "../components/SentenceTrack";
import SentenceViewButton from "../components/SentenceViewButton";
import type { GameRoom, SentenceToken, Turn } from "../game/types";
import type { Lang } from "../i18n";

const DEFAULT_INTERPRET_SECONDS = 60;
const MIN_PLAYERS = 2; // rulebook says 3, but 2 makes testing painless

export default function Room() {
  const { roomCode = "" } = useParams();
  const { t, i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage as Lang) ?? "en";
  const [uid, setUid] = useState<string | null>(null);
  const [room, setRoom] = useState<GameRoom | null>(null);
  const [turns, setTurns] = useState<Turn[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [joining, setJoining] = useState(false);

  // Wait for anonymous auth before subscribing to the room — otherwise the
  // first reads happen unauthenticated and Firestore rejects them.
  useEffect(() => {
    if (!isFirebaseConfigured()) {
      setError(t("lobby.firebaseMissing"));
      return;
    }
    let mounted = true;
    let unsubRoom: (() => void) | undefined;
    let unsubTurns: (() => void) | undefined;
    getCurrentUid()
      .then((u) => {
        if (!mounted) return;
        setUid(u);
        unsubRoom = watchRoom(roomCode, (r) => mounted && setRoom(r));
        unsubTurns = watchTurns(roomCode, (ts) => mounted && setTurns(ts));
      })
      .catch((e) => mounted && setError(String(e)));
    return () => {
      mounted = false;
      unsubRoom?.();
      unsubTurns?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomCode]);

  // Auto-join when someone hits a direct /play/ABCDEF link without coming
  // through the Lobby first. Only attempts during the lobby phase.
  useEffect(() => {
    if (!room || !uid || joining) return;
    if (room.players[uid]) return;
    if (room.phase !== "lobby") return;
    const displayName = (localStorage.getItem("lexikon.displayName") || "").trim()
      || prompt("Pick a display name") || `Player ${Object.keys(room.players).length + 1}`;
    localStorage.setItem("lexikon.displayName", displayName);
    setJoining(true);
    joinRoom({ roomCode: room.code, displayName })
      .catch((e) => setError(String(e)))
      .finally(() => setJoining(false));
  }, [room, uid, joining]);

  if (error) {
    return (
      <div className="card">
        {error}
        <div style={{ marginTop: 12 }}><Link className="btn ghost" to="/play">{t("common.back")}</Link></div>
      </div>
    );
  }

  if (!room || !uid) return <div className="card">{t("common.loading")}</div>;

  const me = room.players[uid];
  if (!me) {
    return (
      <div className="card">
        {room.phase === "lobby" ? <p>Joining…</p> : <>
          <p>This game has already started. Ask the host to wait for the next round, or start a new room.</p>
          <Link className="btn primary" to="/play">{t("lobby.title")}</Link>
        </>}
      </div>
    );
  }

  const composerId = room.turnOrder[room.currentTurnIndex % room.turnOrder.length];
  const currentTurn = turns.find((tt) => tt.index === room.currentTurnIndex);

  return (
    <div>
      <Header room={room} uid={uid} composerId={composerId} />
      {room.phase === "lobby" && <LobbyView room={room} uid={uid} />}
      {room.phase === "finished" && <FinishedView room={room} uid={uid} />}
      {room.phase !== "lobby" && room.phase !== "finished" && currentTurn && (
        <TurnView room={room} uid={uid} turn={currentTurn} lang={lang} />
      )}
      {(room.phase === "compose") && !currentTurn && (
        composerId === uid
          ? <StartTurnView room={room} uid={uid} />
          : <WaitingForComposer composerName={room.players[composerId]?.displayName ?? "Composer"} />
      )}
    </div>
  );
}

function Header({ room, uid, composerId }: { room: GameRoom; uid: string; composerId: string }) {
  const { t } = useTranslation();
  const [confirming, setConfirming] = useState(false);
  const isHost = room.hostId === uid;
  const inPlay = room.phase === "compose" || room.phase === "interpret" || room.phase === "reveal";

  async function endGame() {
    setConfirming(false);
    await updateRoom(room.code, { phase: "finished", endedEarly: true });
  }

  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
      <div>
        <h2 style={{ margin: 0 }}>{t("lobby.title")} — <span style={{ fontFamily: "monospace", letterSpacing: "0.15em" }}>{room.code}</span></h2>
        <div style={{ color: "var(--ink-muted)", fontSize: 13, marginTop: 4 }}>
          {Object.values(room.players).length} player(s) · turn {room.currentTurnIndex + 1} / {room.turnOrder.length * room.rounds} · phase: {room.phase}
        </div>
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end", alignItems: "center" }}>
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap", justifyContent: "flex-end" }}>
          {Object.values(room.players)
            .sort((a, b) => b.score - a.score)
            .map((p) => (
              <span
                key={p.id}
                className="pill"
                style={{
                  background: p.id === composerId ? "var(--accent)" : undefined,
                  color: p.id === composerId ? "var(--ink-dark)" : undefined,
                }}
              >
                {p.displayName}{p.id === uid ? " (you)" : ""} · {p.score}
              </span>
            ))}
        </div>
        {isHost && inPlay && (
          confirming ? (
            <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }} title={t("endgame.confirm")}>
              <button className="btn danger" style={{ padding: "6px 12px" }} onClick={endGame}>{t("endgame.confirmYes")}</button>
              <button className="btn ghost" style={{ padding: "6px 12px" }} onClick={() => setConfirming(false)}>{t("common.cancel")}</button>
            </span>
          ) : (
            <button className="btn ghost" style={{ padding: "6px 12px" }} onClick={() => setConfirming(true)} title={t("endgame.confirm")}>
              {t("endgame.end")}
            </button>
          )
        )}
      </div>
    </div>
  );
}

function LobbyView({ room, uid }: { room: GameRoom; uid: string }) {
  const { t } = useTranslation();
  const isHost = room.hostId === uid;
  const playerCount = Object.values(room.players).length;
  const canStart = isHost && playerCount >= MIN_PLAYERS;
  const currentTimer = room.interpretSeconds === undefined ? 60 : room.interpretSeconds;
  const currentStrictness = room.strictness ?? "normal";
  async function start() {
    await updateRoom(room.code, { phase: "compose", currentTurnIndex: 0 });
  }
  async function changeTimer(seconds: number | null) {
    await updateRoom(room.code, { interpretSeconds: seconds });
  }
  async function changeStrictness(s: "strict" | "normal" | "easy") {
    await updateRoom(room.code, { strictness: s });
  }
  const strictnessLabel: Record<typeof currentStrictness, string> = {
    strict: "Strict — exact wording only",
    normal: "Normal — synonyms accepted (kid = child)",
    easy: "Easy — synonyms + prefix + typo-tolerant",
  };
  const shareUrl = `${window.location.origin}/play/${room.code}`;
  return (
    <div className="card">
      <h3>Waiting for players</h3>
      <p>Share this code: <strong style={{ fontFamily: "monospace", letterSpacing: "0.15em", fontSize: 20 }}>{room.code}</strong></p>
      <p style={{ color: "var(--ink-muted)", fontSize: 13 }}>or send a direct link: <a href={shareUrl} style={{ color: "var(--frame-adj)" }}>{shareUrl}</a></p>
      <ul>
        {Object.values(room.players).map((p) => (
          <li key={p.id}>{p.displayName}{p.id === room.hostId ? " (host)" : ""}{p.id === uid ? " (you)" : ""}</li>
        ))}
      </ul>

      <div style={{ background: "var(--panel-2)", padding: 12, borderRadius: 8, margin: "12px 0" }}>
        <strong style={{ fontSize: 13 }}>Interpretation timer: </strong>
        <span style={{ fontSize: 13 }}>{currentTimer === null ? "Off — composer reveals manually" : `${currentTimer}s per round`}</span>
        {isHost && (
          <div style={{ marginTop: 8 }}>
            <select
              value={currentTimer === null ? "off" : String(currentTimer)}
              onChange={(e) => changeTimer(e.target.value === "off" ? null : Number(e.target.value))}
              style={{ padding: "4px 8px", borderRadius: 6, border: "1px solid #d6dbed" }}
            >
              <option value="off">No timer</option>
              <option value="30">30 seconds</option>
              <option value="60">60 seconds (rulebook)</option>
              <option value="90">90 seconds</option>
              <option value="120">2 minutes</option>
              <option value="300">5 minutes</option>
              <option value="600">10 minutes</option>
            </select>
          </div>
        )}
      </div>

      <div style={{ background: "var(--panel-2)", padding: 12, borderRadius: 8, margin: "12px 0" }}>
        <strong style={{ fontSize: 13 }}>Scoring strictness: </strong>
        <span style={{ fontSize: 13 }}>{strictnessLabel[currentStrictness]}</span>
        {isHost && (
          <div style={{ marginTop: 8 }}>
            <select
              value={currentStrictness}
              onChange={(e) => changeStrictness(e.target.value as "strict" | "normal" | "easy")}
              style={{ padding: "4px 8px", borderRadius: 6, border: "1px solid #d6dbed" }}
            >
              <option value="strict">Strict — exact wording only</option>
              <option value="normal">Normal — synonyms accepted</option>
              <option value="easy">Easy — kids / casual</option>
            </select>
          </div>
        )}
      </div>

      {isHost ? (
        <button className="btn primary" disabled={!canStart} onClick={start}>
          {canStart ? "Start game" : `Need at least ${MIN_PLAYERS} players (have ${playerCount})`}
        </button>
      ) : (
        <span style={{ color: "var(--ink-muted)" }}>{t("interpret.waiting")}</span>
      )}
    </div>
  );
}

function WaitingForComposer({ composerName }: { composerName: string }) {
  return (
    <div className="composer">
      <div>
        <BoardGrid tokens={[]} />
      </div>
      <aside className="panel" style={{ display: "grid", gap: 12, alignContent: "start" }}>
        <h3 style={{ margin: 0 }}>Waiting for {composerName}…</h3>
        <p style={{ color: "var(--ink-muted)", margin: 0 }}>
          They're picking a sentence. While you wait, click any icon to study it — when they place the frames the puzzle will appear.
        </p>
      </aside>
    </div>
  );
}

function StartTurnView({ room, uid }: { room: GameRoom; uid: string }) {
  // Always use the ROOM's language for the sentence ordering, not the picker's UI language.
  const lang = room.language;
  const [picked, setPicked] = useState<string | null>(null);
  const [customCodes, setCustomCodes] = useState<CustomCode[]>([]);

  // AI compose state
  const [aiOpen, setAiOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiKey, setAiKey] = useState(() => getStoredKey());
  const [aiBusy, setAiBusy] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [aiResult, setAiResult] = useState<{ tokens: SentenceToken[]; translation: string } | null>(null);

  useEffect(() => {
    const unsub = watchCustomCodes(setCustomCodes);
    return () => unsub();
  }, []);

  const allCodes = useMemo(() => [...CODES, ...customCodes], [customCodes]);

  async function runAiSuggest() {
    if (!aiPrompt.trim() || !aiKey.trim()) return;
    setAiBusy(true);
    setAiError(null);
    setAiResult(null);
    try {
      setStoredKey(aiKey.trim());
      const { tokens: suggested, translation } = await suggestCode({
        targetSentence: aiPrompt.trim(),
        apiKey: aiKey.trim(),
      });
      const newTokens = suggestedTokensToSentenceTokens(suggested);
      if (newTokens.length === 0) {
        setAiError("Gemini didn't return any usable icons. Try rephrasing.");
        return;
      }
      setAiResult({ tokens: newTokens, translation });
    } catch (e) {
      setAiError(e instanceof Error ? e.message : String(e));
    } finally {
      setAiBusy(false);
    }
  }

  async function commitAi() {
    if (!aiResult) return;
    setPicked("ai");
    const seconds = room.interpretSeconds === undefined ? DEFAULT_INTERPRET_SECONDS : room.interpretSeconds;
    const turn: Turn = {
      index: room.currentTurnIndex,
      composerId: uid,
      mode: "invent",
      sentence: aiResult.tokens,
      composerOwnTranslation: aiResult.translation,
      composeStartAt: Date.now(),
      interpretStartAt: Date.now(),
      interpretations: {},
    };
    if (seconds !== null) {
      turn.interpretEndAt = Date.now() + seconds * 1000;
    }
    await writeTurn(room.code, turn);
    await updateRoom(room.code, { phase: "interpret" });
  }

  async function pickCode(codeId: string) {
    setPicked(codeId);
    const code = allCodes.find((c) => c.id === codeId);
    if (!code) return;
    const seconds = room.interpretSeconds === undefined ? DEFAULT_INTERPRET_SECONDS : room.interpretSeconds;
    const turn: Turn = {
      index: room.currentTurnIndex,
      composerId: uid,
      mode: "code",
      codeId,
      sentence: getOrderedTokensForLang(code, lang),
      composeStartAt: Date.now(),
      interpretStartAt: Date.now(),
      interpretations: {},
    };
    if (seconds !== null) {
      turn.interpretEndAt = Date.now() + seconds * 1000;
    }
    await writeTurn(room.code, turn);
    await updateRoom(room.code, { phase: "interpret" });
  }
  async function pickRandom() {
    const code = allCodes.length > 0
      ? allCodes[Math.floor(Math.random() * allCodes.length)]
      : getRandomCode();
    await pickCode(code.id);
  }
  return (
    <div className="card">
      <h3>Pick a sentence to compose</h3>
      <p style={{ color: "var(--ink-muted)" }}>
        One sentence per turn. Others won't see your pick until you confirm.
        {customCodes.length > 0 && <> Custom codes from the library are included.</>}
      </p>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button className="btn primary" onClick={pickRandom} disabled={!!picked}>🎲 Pick a random code →</button>
        <button className="btn ghost" onClick={() => { setAiOpen(true); setAiResult(null); setAiError(null); }} disabled={!!picked}>✨ Compose with AI</button>
      </div>

      {aiOpen && (
        <div className="zoom-backdrop" onClick={() => setAiOpen(false)}>
          <div className="zoom-card" style={{ maxWidth: 600 }} onClick={(e) => e.stopPropagation()}>
            <button className="zoom-close" onClick={() => setAiOpen(false)}>×</button>
            <h3 style={{ margin: 0 }}>Compose a sentence with Gemini</h3>
            <p style={{ color: "var(--ink-muted)", fontSize: 13, margin: 0 }}>
              Describe the sentence you want. Gemini picks icons and orders them for you. Review before locking it in for the round.
            </p>
            <label className="field">
              Target sentence (English)
              <input
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                placeholder="e.g. The young scientist dreams of a peaceful world"
                autoFocus
              />
            </label>
            <label className="field">
              Gemini API key
              <input
                type="password"
                value={aiKey}
                onChange={(e) => setAiKey(e.target.value)}
                placeholder="Get one at aistudio.google.com/apikey"
              />
            </label>
            <p style={{ color: "var(--ink-muted)", fontSize: 11, margin: 0 }}>
              ⚠ Key is stored in your browser localStorage and called directly from your client.
            </p>

            {aiError && (
              <div style={{ background: "#fde2e1", color: "#9b2520", padding: 8, borderRadius: 6, fontSize: 13 }}>{aiError}</div>
            )}

            {aiResult && (
              <div style={{ background: "var(--panel-2)", padding: 12, borderRadius: 8, display: "grid", gap: 8 }}>
                <strong style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--ink-muted)" }}>Gemini suggested</strong>
                <p style={{ margin: 0, fontStyle: "italic" }}>"{aiResult.translation}"</p>
                <SentenceTrack tokens={aiResult.tokens} showGloss />
              </div>
            )}

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {!aiResult ? (
                <button className="btn primary" onClick={runAiSuggest} disabled={!aiPrompt.trim() || !aiKey.trim() || aiBusy}>
                  {aiBusy ? "Asking Gemini…" : "Suggest"}
                </button>
              ) : (
                <>
                  <button className="btn primary" onClick={commitAi}>Use this for the round →</button>
                  <button className="btn ghost" onClick={() => { setAiResult(null); }}>Try again</button>
                </>
              )}
              <button className="btn ghost" onClick={() => setAiOpen(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      <details style={{ marginTop: 16 }}>
        <summary style={{ cursor: "pointer" }}>Browse all {allCodes.length} codes</summary>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 6, marginTop: 12 }}>
          {allCodes.map((c) => (
            <button
              key={c.id}
              className="btn ghost"
              onClick={() => pickCode(c.id)}
              disabled={!!picked}
              style={{ padding: "8px 10px", justifyContent: "flex-start", textAlign: "left", flexDirection: "column", alignItems: "flex-start" }}
            >
              <span style={{ fontFamily: "monospace", fontSize: 11, color: "var(--ink-muted)" }}>#{c.id} · {c.iconCount} icons</span>
              <span style={{ fontSize: 13 }}>{c.translations[lang] ?? c.translations.en}</span>
            </button>
          ))}
        </div>
      </details>
    </div>
  );
}

function TurnView({ room, uid, turn }: { room: GameRoom; uid: string; turn: Turn; lang: Lang }) {
  const { t } = useTranslation();
  // The room's language is authoritative for translations and word order.
  const lang = room.language;
  const isComposer = turn.composerId === uid;
  const [customCodes, setCustomCodes] = useState<CustomCode[]>([]);
  useEffect(() => {
    const unsub = watchCustomCodes(setCustomCodes);
    return () => unsub();
  }, []);
  const code = useMemo(() => {
    if (turn.codeId) {
      const found = [...CODES, ...customCodes].find((c) => c.id === turn.codeId);
      if (found) return found;
    }
    // Invented (AI-composed or hand-invented) — synthesize from turn data.
    return syntheticCodeFromTurn(turn);
  }, [turn.codeId, turn.sentence, turn.composerOwnTranslation, customCodes, turn]);
  const sentence = turn.sentence ?? (code ? getOrderedTokensForLang(code, lang) : []);
  const [guess, setGuess] = useState(turn.interpretations[uid] ?? "");
  const submitted = !!turn.interpretations[uid];

  async function submit() {
    if (!guess.trim() || submitted) return;
    await submitInterpretation(room.code, turn.index, uid, guess.trim());
  }

  async function doReveal() {
    if (!code) return;
    const points = scoreTurnCoded(code, lang, turn.interpretations, room.strictness ?? "normal");
    const newScores: Record<string, number> = {};
    for (const [pid, p] of Object.entries(room.players)) {
      const delta = points[pid] ?? 0;
      newScores[pid] = (p.score ?? 0) + delta;
    }
    await revealAndScore(room.code, turn.index, points, newScores);
  }

  // Composer auto-reveals if (a) timer expired or (b) all non-composers submitted.
  // If there's no timer (room.interpretSeconds === null), only the "all submitted"
  // condition triggers auto-reveal; otherwise composer reveals manually.
  const submittersKey = Object.keys(turn.interpretations ?? {}).sort().join(",");
  useEffect(() => {
    if (!isComposer) return;
    if (room.phase !== "interpret") return;

    const nonComposers = Object.keys(room.players).filter((pid) => pid !== turn.composerId);
    const interpretations = turn.interpretations ?? {};
    const allSubmitted = nonComposers.length > 0 && nonComposers.every((pid) => Boolean(interpretations[pid]));
    if (allSubmitted) {
      doReveal();
      return;
    }

    // No timer configured → wait for manual reveal or all-submitted.
    if (!turn.interpretEndAt || typeof turn.interpretEndAt !== "number") return;

    const ms = turn.interpretEndAt - Date.now();
    if (ms <= 0) {
      // Stale end-time on a fresh turn — give it the full window instead of insta-revealing.
      const age = Date.now() - (turn.interpretStartAt ?? turn.composeStartAt ?? Date.now());
      const fallback = (room.interpretSeconds ?? DEFAULT_INTERPRET_SECONDS) * 1000;
      if (age < 2000) {
        const id = window.setTimeout(() => doReveal(), fallback);
        return () => window.clearTimeout(id);
      }
      doReveal();
      return;
    }
    const id = window.setTimeout(() => doReveal(), ms);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isComposer, room.phase, submittersKey, turn.interpretEndAt]);

  async function nextTurn() {
    const total = room.turnOrder.length * room.rounds;
    if (room.currentTurnIndex + 1 >= total) {
      await updateRoom(room.code, { phase: "finished" });
    } else {
      await updateRoom(room.code, { phase: "compose", currentTurnIndex: room.currentTurnIndex + 1 });
    }
  }

  return (
    <div className="composer">
      <div>
        <BoardGrid tokens={sentence} showSolutionGloss={room.phase === "reveal"} />
      </div>
      <aside className="panel" style={{ display: "grid", gap: 12, alignContent: "start" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <h3 style={{ margin: 0 }}>
            {room.phase === "reveal" ? t("reveal.official") : isComposer ? "You composed" : t("interpret.writeNow")}
          </h3>
          {room.phase === "interpret" && turn.interpretEndAt && (
            <Timer endAt={turn.interpretEndAt} totalSeconds={room.interpretSeconds ?? DEFAULT_INTERPRET_SECONDS} />
          )}
          {room.phase === "interpret" && !turn.interpretEndAt && (
            <span className="pill">No timer</span>
          )}
        </div>
        <SentenceTrack tokens={sentence} />
        <SentenceViewButton tokens={sentence} />

        {room.phase === "interpret" && !isComposer && (
          <>
            <textarea
              rows={4}
              value={guess}
              onChange={(e) => setGuess(e.target.value)}
              placeholder={t("solo.yourGuessPlaceholder")}
              style={{ width: "100%", padding: 10, borderRadius: 8, border: "1px solid #d6dbed" }}
              disabled={submitted}
            />
            <button className="btn primary" onClick={submit} disabled={!guess.trim() || submitted}>
              {submitted ? t("interpret.waiting") : t("interpret.submit")}
            </button>
          </>
        )}

        {room.phase === "interpret" && isComposer && (
          <>
            <p style={{ color: "var(--ink-muted)" }}>{t("interpret.waiting")} <br /><small>(or click below to reveal now)</small></p>
            <button className="btn ghost" onClick={doReveal}>Reveal now</button>
          </>
        )}

        {room.phase === "reveal" && code && (
          <div style={{ display: "grid", gap: 10 }}>
            <p style={{ background: "var(--panel-2)", padding: 12, borderRadius: 8, fontStyle: "italic", margin: 0 }}>
              "{formatSentence(code.translations[lang] ?? code.translations.en)}"
            </p>
            <h3 style={{ margin: "8px 0 0" }}>Answers + points</h3>
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {Object.entries(turn.interpretations).map(([pid, txt]) => {
                const pts = turn.pointsAwarded?.[pid] ?? 0;
                const breakdown = scoreSolo(code, lang, txt, room.strictness ?? "normal");
                return (
                  <li key={pid} style={{ padding: "10px 0", borderBottom: "1px solid #eee" }}>
                    <div style={{ display: "flex", gap: 8, alignItems: "baseline", marginBottom: 6 }}>
                      <strong style={{ minWidth: 80 }}>{room.players[pid]?.displayName ?? pid}</strong>
                      <span style={{ flex: 1 }}>{txt}</span>
                      <span className={`pill ${pts > 0 ? "ok" : pts < 0 ? "danger" : ""}`}>{pts >= 0 ? "+" : ""}{pts}</span>
                    </div>
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 4 }}>
                      {breakdown.perIcon.map((p) => (
                        <span
                          key={p.iconKey}
                          className={`pill ${p.matched ? "ok" : "danger"}`}
                          title={p.gloss}
                          style={{ fontSize: 10 }}
                        >
                          {p.matched ? "✓" : "✗"} {p.iconKey} · {p.gloss}
                        </span>
                      ))}
                      <span
                        className={`pill ${breakdown.sentenceCorrect ? "ok" : "danger"}`}
                        style={{ fontSize: 10 }}
                        title="Whole sentence match"
                      >
                        sentence {breakdown.sentenceCorrect ? "+1 bonus" : "−1 malus"}
                      </span>
                    </div>
                  </li>
                );
              })}
              {Object.keys(turn.interpretations).length === 0 && (
                <li style={{ color: "var(--ink-muted)" }}>Nobody submitted in time.</li>
              )}
            </ul>
            {isComposer && (
              <button className="btn primary" onClick={nextTurn}>
                {room.currentTurnIndex + 1 >= room.turnOrder.length * room.rounds ? t("endgame.end") : `${t("reveal.nextRound")} →`}
              </button>
            )}
          </div>
        )}
      </aside>
    </div>
  );
}

function FinishedView({ room, uid }: { room: GameRoom; uid: string }) {
  const { t } = useTranslation();
  const sorted = Object.values(room.players).sort((a, b) => b.score - a.score);
  const topScore = sorted[0]?.score ?? 0;
  const winners = sorted.filter((p) => p.score === topScore);
  const isTie = winners.length > 1 && sorted.length > 1;
  const medals = ["🥇", "🥈", "🥉"];

  // Standard competition ranking: equal scores share a rank (1, 2, 2, 4…).
  let lastScore: number | null = null;
  let lastRank = 0;
  const ranked = sorted.map((p, i) => {
    const rank = p.score === lastScore ? lastRank : i + 1;
    lastScore = p.score;
    lastRank = rank;
    return { p, rank };
  });

  return (
    <div className="card scoreboard">
      <div className="scoreboard__head">
        <div className="scoreboard__trophy">🏆</div>
        <h2 className="scoreboard__title">{t("endgame.title")}</h2>
        <p className="scoreboard__winner">
          {isTie ? t("endgame.tie") : t("endgame.winner", { name: winners[0]?.displayName ?? "—" })}
        </p>
        {room.endedEarly && <p className="scoreboard__note">{t("endgame.endedEarly")}</p>}
      </div>

      <h3 className="scoreboard__subtitle">{t("endgame.finalScores")}</h3>
      <ol className="scoreboard__list">
        {ranked.map(({ p, rank }) => (
          <li key={p.id} className={`scoreboard__row${p.score === topScore ? " scoreboard__row--winner" : ""}`}>
            <span className="scoreboard__rank">{rank <= 3 ? medals[rank - 1] : `${rank}.`}</span>
            <span className="scoreboard__name">
              {p.displayName}
              {p.id === uid ? ` (${t("endgame.you")})` : ""}
              {p.isBot ? " 🤖" : ""}
            </span>
            <span className="scoreboard__score">{p.score} <small>{t("endgame.points")}</small></span>
          </li>
        ))}
      </ol>

      <Link className="btn primary" to="/play">{t("endgame.playAgain")}</Link>
    </div>
  );
}
